import React from 'react';
import {
  Pane,
  Card,
  Badge,
  Text,
  Heading,
  Strong,
  Spinner,
  Button,
  ChevronDownIcon,
  ChevronRightIcon,
  ArrowRightIcon,
  TickIcon,
  TrashIcon,
  IconButton
} from 'evergreen-ui';
import { CommentSection } from '../../Comments';
import { getSeverityBackgroundColor } from '../utils';

export const FindingsTab = ({
  findings,
  isLoading,
  hasMore,
  searchQuery,
  commentCounts,
  expandedComments,
  manuallyCollapsedComments,
  toggleCommentsExpansion,
  collapseComments,
  actionLoading,
  handleAction,
  openEntityOverview,
  currentProject,
  currentUser
}) => {
  return (
    <Pane>
      <Pane display="flex" alignItems="center" justifyContent="space-between" marginBottom={16}>
        <Heading size={600}>
          Findings {findings.length > 0 && `(${findings.length} shown${!searchQuery && hasMore ? ', more available' : ''})`}
        </Heading>
      </Pane>
      
      {findings.length === 0 && !isLoading ? (
        <Card background="tint1" padding={32} textAlign="center">
          <Text color="muted">
            {searchQuery ? 'No findings match your search' : 'No findings found'}
          </Text>
        </Card>
      ) : (
        <Pane>
          {findings.map(finding => (
            <React.Fragment key={finding.id}>
              <Card
                background={finding.is_new ? "#E6F4EA" : (finding.is_checked ? "#F7F8FA" : "white")}
                border={finding.is_new ? "2px solid #47B881" : "1px solid #E6E8F0"}
                padding={16}
                marginBottom={8}
                elevation={0}
              >
                <Pane display="flex" alignItems="flex-start" justifyContent="space-between">
                  <Pane flex={1}>
                    <Pane display="flex" alignItems="center" marginBottom={8}>
                      <Strong size={500} marginRight={8}>{finding.name}</Strong>
                      <Badge
                        backgroundColor={getSeverityBackgroundColor(finding.severity)}
                        color={finding.severity === 'critical' ? 'red' : 'default'}
                        marginRight={8}
                      >
                        {finding.severity || 'none'}
                      </Badge>
                      {finding.is_new && (
                        <Badge backgroundColor="#47B881" color="white" marginRight={8}>
                          NEW
                        </Badge>
                      )}
                      {finding.is_checked && (
                        <Badge backgroundColor="#999999" color="white">
                          CHECKED
                        </Badge>
                      )}
                    </Pane>
                    
                    {finding.description && (
                      <Text size={300} color="muted" marginBottom={8}>
                        {finding.description}
                      </Text>
                    )}
                    
                    <Pane display="flex" flexWrap="wrap" gap={8} marginBottom={8}>
                      <Badge backgroundColor="#F7F8FA" color="default">
                        {finding.source_type}: {finding.source_name}
                      </Badge>
                      {finding.template_id && (
                        <Badge backgroundColor="#E6F3FF" color="blue">
                          Template: {finding.template_id}
                        </Badge>
                      )}
                      {finding.url && (
                        <Badge backgroundColor="#FFF4E6" color="orange">
                          URL: {finding.url}
                        </Badge>
                      )}
                    </Pane>
                  </Pane>
                  
                  <Pane display="flex" flexDirection="column" gap={8}>
                    <Button
                      size="small"
                      appearance="minimal"
                      iconBefore={ArrowRightIcon}
                      onClick={() => openEntityOverview('finding', finding.id, { 
                        name: finding.name,
                        severity: finding.severity,
                        type: 'finding'
                      })}
                    >
                      View Details
                    </Button>
                    <Button
                      size="small"
                      appearance="minimal"
                      iconBefore={expandedComments[`finding_id_${finding.id}`] ? ChevronDownIcon : ChevronRightIcon}
                      onClick={() => toggleCommentsExpansion(`finding_id_${finding.id}`)}
                    >
                      Comments
                      {commentCounts[`finding_id_${finding.id}`] > 0 && (
                        <Badge marginLeft={8} backgroundColor="#1070CA" color="white">
                          {commentCounts[`finding_id_${finding.id}`]}
                        </Badge>
                      )}
                    </Button>
                    <Button
                      size="small"
                      iconBefore={TickIcon}
                      intent={finding.is_checked ? "success" : "default"}
                      isLoading={actionLoading[`mark_checked_finding_${finding.id}`]}
                      onClick={() => handleAction('mark_checked', 'finding', finding.id, {
                        name: finding.name
                      })}
                    >
                      {finding.is_checked ? 'Uncheck' : 'Mark Checked'}
                    </Button>
                    <IconButton
                      icon={TrashIcon}
                      intent="danger"
                      size="small"
                      appearance="minimal"
                      isLoading={actionLoading[`delete_finding_${finding.id}`]}
                      onClick={() => {
                        if (window.confirm(`Delete finding "${finding.name}"?`)) {
                          handleAction('delete', 'finding', finding.id);
                        }
                      }}
                    />
                  </Pane>
                </Pane>
              </Card>
              
              <CommentSection
                projectId={currentProject.id}
                entityType="finding_id"
                entityId={finding.id}
                currentUserId={currentUser?.id}
                compact={true}
                forceShow={expandedComments[`finding_id_${finding.id}`] || false}
                manuallyCollapsed={manuallyCollapsedComments[`finding_id_${finding.id}`] || false}
                onAutoExpand={(entityId) => {
                  toggleCommentsExpansion(entityId);
                }}
                onCollapse={collapseComments}
              />
            </React.Fragment>
          ))}
          
          {isLoading && (
            <Pane display="flex" justifyContent="center" padding={16}>
              <Spinner />
            </Pane>
          )}
        </Pane>
      )}
    </Pane>
  );
};

